"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import { Mail, Send, Inbox, Clock, CheckCircle, XCircle, Eye, MousePointerClick, Reply, Paperclip, X, Download, FileImage, ExternalLink } from "lucide-react";
import { useRouter } from "next/navigation";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import { sendEmail } from "@/app/(authenticated)/communication/_modules/services/sendEmail";
import type { SendEmailRequest, EmailAttachment } from "@/app/(authenticated)/communication/_modules/types/sendEmail.types";
import type { EmailHistory, EmailAttachment as ReceivedEmailAttachment } from "@/app/(authenticated)/communication/_modules/types/email-history.types";
import { oauthConnectionService } from "@/app/_module/services/oauthConnectionService";
import type { EmailOAuthConnection } from "@/app/_module/types/email-oauth";
import { communicationService } from "@/app/_module/services/communicationService";

interface EmailDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: EmailHistory | null;
  leadId?: string;
}

// Max file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024;

export const EmailDetailModal: React.FC<EmailDetailModalProps> = function({
  open,
  onOpenChange,
  email,
  leadId,
}) {
  const router = useRouter();
  const [isReplying, setIsReplying] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [oauthConnection, setOauthConnection] = useState<EmailOAuthConnection | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Attachment download/preview state
  const [downloadingAttachment, setDownloadingAttachment] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{
    filename: string;
    contentType: string;
    content: string;
  } | null>(null);
  // Fresh attachment info from provider (with IDs for downloading)
  const [refreshedAttachments, setRefreshedAttachments] = useState<ReceivedEmailAttachment[] | null>(null);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  // Loaded inline image previews (keyed by attachment id or filename)
  const [loadedImagePreviews, setLoadedImagePreviews] = useState<Record<string, string>>({});

  // Reset state when modal opens/closes or email changes
  useEffect(() => {
    if (!open) {
      setIsReplying(false);
      setReplyBody("");
      setError("");
      setAttachments([]);
      setAttachmentFiles([]);
      setPreviewAttachment(null);
      setRefreshedAttachments(null);
      setLoadedImagePreviews({});
    }
  }, [open, email?.id]);

  // Track email open when modal is opened
  useEffect(() => {
    if (open && email?.id) {
      // Mark the email as opened (fire and forget - don't block UI)
      communicationService.markEmailOpened(email.id).catch(() => {
        // Silently fail - this is just tracking, shouldn't affect UX
      });
    }
  }, [open, email?.id]);

  // Fetch fresh attachment info if email has attachments but they're missing IDs
  useEffect(() => {
    const fetchAttachmentInfo = async () => {
      if (!open || !email) return;

      const messageId = email.providerMessageId || email.messageId;
      const hasAttachments = email.attachments && email.attachments.length > 0;
      const attachmentsMissingIds = email.attachments?.some(att => !att.id);

      // Only fetch if we have attachments but some are missing IDs
      if (hasAttachments && attachmentsMissingIds && messageId) {
        setLoadingAttachments(true);
        try {
          const result = await communicationService.getEmailAttachments(messageId);
          if (result.attachments) {
            setRefreshedAttachments(result.attachments);
          }
        } catch (err) {
          // Silently fail - we'll fall back to showing attachments without download capability
          console.warn('Failed to fetch fresh attachment info:', err);
        } finally {
          setLoadingAttachments(false);
        }
      }
    };

    fetchAttachmentInfo();
  }, [open, email]);

  // Check if content type is browser-displayable image (not HEIC/HEIF)
  const isBrowserDisplayableImage = (contentType: string, filename?: string): boolean => {
    // HEIC/HEIF files can't be displayed by most browsers
    const heicExtensions = ['.heic', '.heif'];
    if (filename && heicExtensions.some(ext => filename.toLowerCase().endsWith(ext))) {
      return false;
    }
    // Also check content type
    if (contentType?.includes('heic') || contentType?.includes('heif')) {
      return false;
    }
    // Standard browser-displayable image types
    return contentType?.startsWith('image/') ?? false;
  };

  // Auto-load image previews when modal opens and attachments are available
  useEffect(() => {
    const loadImagePreviews = async () => {
      if (!open || !email) return;

      const messageId = email.providerMessageId || email.messageId;
      if (!messageId) return;

      // Only load browser-displayable images (excludes HEIC/HEIF)
      const imageAttachments = email.attachments?.filter(att =>
        isBrowserDisplayableImage(att.contentType, att.filename)
      ) || [];

      if (imageAttachments.length === 0) return;

      // Load each image attachment
      for (const attachment of imageAttachments) {
        // Get attachment ID (from original or refreshed)
        let attachmentId = attachment.id;
        if (!attachmentId && refreshedAttachments) {
          const refreshedAtt = refreshedAttachments.find(ra => ra.filename === attachment.filename);
          attachmentId = refreshedAtt?.id;
        }

        if (!attachmentId) continue;

        // Skip if already loaded
        const key = attachment.id || attachment.filename;
        if (loadedImagePreviews[key]) continue;

        try {
          const result = await communicationService.downloadEmailAttachment(messageId, attachmentId);
          setLoadedImagePreviews(prev => ({
            ...prev,
            [key]: `data:${result.contentType};base64,${result.content}`
          }));
        } catch (err) {
          console.warn(`Failed to load image preview for ${attachment.filename}:`, err);
        }
      }
    };

    // Only load after refreshed attachments are available (if needed) or if attachments already have IDs
    const hasAttachments = email?.attachments && email.attachments.length > 0;
    const allHaveIds = email?.attachments?.every(att => att.id);

    if (hasAttachments && (allHaveIds || refreshedAttachments)) {
      loadImagePreviews();
    }
  }, [open, email, refreshedAttachments, loadedImagePreviews]);

  // Check OAuth connection when starting to reply
  const handleStartReply = async () => {
    setCheckingConnection(true);
    setError("");
    try {
      const status = await oauthConnectionService.getConnectionStatus();
      const googleConnected = status.google?.status === "connected";
      const microsoftConnected = status.microsoft?.status === "connected";

      if (googleConnected) {
        setOauthConnection(status.google!);
      } else if (microsoftConnected) {
        setOauthConnection(status.microsoft!);
      } else {
        setError("No email account connected. Please connect your Gmail or Microsoft account in Settings to send emails.");
        setCheckingConnection(false);
        return;
      }
      setIsReplying(true);
    } catch {
      setError("Failed to check email connection status.");
    } finally {
      setCheckingConnection(false);
    }
  };

  // Convert File to base64
  const fileToAttachment = (file: File): Promise<EmailAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve({
          filename: file.name,
          content: base64,
          contentType: file.type || 'application/octet-stream',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentSize = attachmentFiles.reduce((sum, f) => sum + f.size, 0);

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" is too large. Max size is 10MB.`);
        continue;
      }
      if (currentSize + file.size > MAX_TOTAL_SIZE) {
        setError("Total attachment size exceeds 25MB limit.");
        break;
      }
      if (attachmentFiles.some(f => f.name === file.name)) {
        continue;
      }
      try {
        const attachment = await fileToAttachment(file);
        setAttachments(prev => [...prev, attachment]);
        setAttachmentFiles(prev => [...prev, file]);
      } catch {
        setError(`Failed to read file "${file.name}".`);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Send reply
  const handleSendReply = async () => {
    if (!email || !replyBody.trim() || !oauthConnection) return;

    setSending(true);
    setError("");

    // Determine reply-to address
    const replyToEmail = email.emailType === "inbound" ? email.fromEmail : email.toEmail;

    const payload: SendEmailRequest = {
      to: replyToEmail,
      subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      body: replyBody,
      useOAuth: true,
      provider: oauthConnection.provider,
      leadId: leadId || email.leadId,
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    try {
      await sendEmail(payload);
      setIsReplying(false);
      setReplyBody("");
      setAttachments([]);
      setAttachmentFiles([]);
      onOpenChange(false);
    } catch {
      setError("Failed to send reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      sent: "bg-blue-100 text-blue-800",
      delivered: "bg-green-100 text-green-800",
      received: "bg-green-100 text-green-800",
      opened: "bg-purple-100 text-purple-800",
      clicked: "bg-indigo-100 text-indigo-800",
      failed: "bg-red-100 text-red-800",
      bounced: "bg-orange-100 text-orange-800",
      queued: "bg-gray-100 text-gray-800",
      sending: "bg-blue-100 text-blue-800",
    };

    return (
      <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </Badge>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <Send className="h-4 w-4 text-blue-600" />;
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "received":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "opened":
        return <Eye className="h-4 w-4 text-purple-600" />;
      case "clicked":
        return <MousePointerClick className="h-4 w-4 text-indigo-600" />;
      case "failed":
      case "bounced":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Mail className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format email body for display - show full content
  const formatEmailBody = (bodyText: string | null | undefined, bodyHtml: string | null | undefined): string => {
    // Prefer plain text for cleaner display
    let text = bodyText || '';

    // If no plain text, extract from HTML
    if (!text && bodyHtml) {
      text = bodyHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    if (!text) return '(No content)';

    // Clean up excessive whitespace but keep the full content
    text = text.replace(/\n{4,}/g, '\n\n\n');

    return text.trim() || '(No content)';
  };

  // Get file icon based on content type
  const getFileIcon = (contentType: string): string => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.includes('pdf')) return '📄';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('sheet') || contentType.includes('excel')) return '📊';
    if (contentType.includes('presentation') || contentType.includes('powerpoint')) return '📑';
    if (contentType.includes('zip') || contentType.includes('archive')) return '📦';
    if (contentType.startsWith('video/')) return '🎥';
    if (contentType.startsWith('audio/')) return '🎵';
    return '📎';
  };

  // Format attachment file size
  const formatAttachmentSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper to get email status
  const getEmailStatus = (email: EmailHistory): string => {
    return email.emailStatus || email.status || 'unknown';
  };

  // Download or preview attachment
  const handleAttachmentClick = async (attachment: ReceivedEmailAttachment) => {
    if (!email) {
      setError("Cannot download attachment - missing email information");
      return;
    }

    const messageId = email.providerMessageId || email.messageId;
    if (!messageId) {
      setError("Cannot download attachment - missing message ID");
      return;
    }

    // Try to find attachment ID from refreshed attachments if original doesn't have one
    let attachmentId = attachment.id;
    if (!attachmentId && refreshedAttachments) {
      // Match by filename
      const refreshedAtt = refreshedAttachments.find(
        ra => ra.filename === attachment.filename
      );
      if (refreshedAtt?.id) {
        attachmentId = refreshedAtt.id;
      }
    }

    if (!attachmentId) {
      setError("Cannot download attachment - attachment ID not available. Please try again.");
      return;
    }

    setDownloadingAttachment(attachmentId);
    setError("");

    try {
      const result = await communicationService.downloadEmailAttachment(
        messageId,
        attachmentId
      );

      // For browser-displayable images, show preview; otherwise download
      if (isBrowserDisplayableImage(result.contentType, result.filename)) {
        setPreviewAttachment({
          filename: result.filename,
          contentType: result.contentType,
          content: result.content,
        });
      } else {
        // For HEIC, HEIF, PDFs, and other files, trigger download
        downloadFile(result.filename, result.contentType, result.content);
      }
    } catch (err) {
      setError("Failed to download attachment. Please try again.");
    } finally {
      setDownloadingAttachment(null);
    }
  };

  // Download a file from base64 content
  const downloadFile = (filename: string, contentType: string, base64Content: string) => {
    // Convert base64 to blob
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Download the previewed image
  const downloadPreviewedImage = () => {
    if (previewAttachment) {
      downloadFile(
        previewAttachment.filename,
        previewAttachment.contentType,
        previewAttachment.content
      );
    }
  };

  if (!email) return null;

  const status = getEmailStatus(email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {email.emailType === "inbound" ? (
              <Inbox className="h-5 w-5 text-green-600" />
            ) : (
              <Send className="h-5 w-5 text-blue-600" />
            )}
            <span className="truncate">{email.subject || "(No subject)"}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Email details and reply options
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email metadata */}
          <div className="space-y-2 p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(status)}
                {getStatusBadge(status)}
                {email.attachmentCount && email.attachmentCount > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Paperclip className="h-3 w-3 mr-1" />
                    {email.attachmentCount} file{email.attachmentCount !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {formatDate(email.sentAt)}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium">From: </span>
                <span className="text-muted-foreground">{email.fromEmail}</span>
              </div>
              <div>
                <span className="font-medium">To: </span>
                <span className="text-muted-foreground">{email.toEmail}</span>
              </div>
              {email.ccEmails && email.ccEmails.length > 0 && (
                <div className="col-span-2">
                  <span className="font-medium">CC: </span>
                  <span className="text-muted-foreground">{email.ccEmails.join(", ")}</span>
                </div>
              )}
            </div>

            {/* Engagement tracking */}
            {email.emailType === "outbound" && (email.openedAt || email.clickedAt || email.deliveredAt) && (
              <div className="flex items-center gap-3 pt-2 border-t text-xs text-muted-foreground">
                {email.deliveredAt && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    Delivered {formatDate(email.deliveredAt)}
                  </span>
                )}
                {email.openedAt && (
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3 text-purple-500" />
                    Opened {formatDate(email.openedAt)}
                  </span>
                )}
                {email.clickedAt && (
                  <span className="flex items-center gap-1">
                    <MousePointerClick className="h-3 w-3 text-indigo-500" />
                    Clicked {formatDate(email.clickedAt)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Email body */}
          <div className="border rounded-lg p-4">
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-sm m-0 bg-transparent p-0">
                {formatEmailBody(email.bodyText, email.bodyHtml)}
              </pre>
            </div>
          </div>

          {/* Attachments section */}
          {email.attachments && email.attachments.length > 0 && (() => {
            // Only show browser-displayable images in the image grid (excludes HEIC/HEIF)
            const imageAttachments = email.attachments.filter(att =>
              isBrowserDisplayableImage(att.contentType, att.filename)
            );
            // Other files include HEIC/HEIF and all non-image files
            const otherAttachments = email.attachments.filter(att =>
              !isBrowserDisplayableImage(att.contentType, att.filename)
            );

            return (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Attachments ({email.attachments.length})
                  </span>
                  {loadingAttachments && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </div>

                {/* Image attachments - shown as inline previews */}
                {imageAttachments.length > 0 && (
                  <div className="space-y-3">
                    <span className="text-xs text-muted-foreground font-medium">Images</span>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {imageAttachments.map((attachment, index) => {
                        const key = attachment.id || attachment.filename;
                        const previewUrl = loadedImagePreviews[key];
                        const hasAttachmentId = attachment.id ||
                          (refreshedAttachments?.find(ra => ra.filename === attachment.filename)?.id);
                        const isDownloading = downloadingAttachment === attachment.id ||
                          (refreshedAttachments?.find(ra => ra.filename === attachment.filename)?.id === downloadingAttachment);

                        return (
                          <div
                            key={`img-${attachment.filename}-${index}`}
                            className="relative group rounded-lg overflow-hidden bg-muted border"
                          >
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                alt={attachment.filename}
                                className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => handleAttachmentClick(attachment)}
                              />
                            ) : (
                              <div className="w-full h-32 flex items-center justify-center bg-muted">
                                {isDownloading || loadingAttachments ? (
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                ) : (
                                  <FileImage className="h-8 w-8 text-muted-foreground" />
                                )}
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                              <p className="text-xs text-white truncate">{attachment.filename}</p>
                              <p className="text-[10px] text-white/70">{formatAttachmentSize(attachment.size)}</p>
                            </div>
                            {hasAttachmentId && (
                              <button
                                onClick={() => handleAttachmentClick(attachment)}
                                disabled={isDownloading}
                                className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Other attachments - shown as list */}
                {otherAttachments.length > 0 && (
                  <div className="space-y-2">
                    {imageAttachments.length > 0 && (
                      <span className="text-xs text-muted-foreground font-medium">Files</span>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {otherAttachments.map((attachment, index) => {
                        const hasAttachmentId = attachment.id ||
                          (refreshedAttachments?.find(ra => ra.filename === attachment.filename)?.id);
                        const isDownloading = downloadingAttachment === attachment.id ||
                          (refreshedAttachments?.find(ra => ra.filename === attachment.filename)?.id === downloadingAttachment);

                        return (
                          <button
                            key={`file-${attachment.filename}-${index}`}
                            onClick={() => handleAttachmentClick(attachment)}
                            disabled={isDownloading || loadingAttachments}
                            className="flex items-center gap-2 p-2 bg-muted rounded-md hover:bg-muted/80 transition-colors text-left w-full disabled:opacity-50"
                          >
                            {isDownloading ? (
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground shrink-0" />
                            ) : (
                              <span className="text-lg shrink-0">{getFileIcon(attachment.contentType)}</span>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{attachment.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatAttachmentSize(attachment.size)}
                              </p>
                            </div>
                            {hasAttachmentId && (
                              <Download className="h-4 w-4 text-muted-foreground shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Image preview */}
          {previewAttachment && (
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileImage className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium truncate">{previewAttachment.filename}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadPreviewedImage}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewAttachment(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-center bg-muted/50 rounded-md p-2">
                <img
                  src={`data:${previewAttachment.contentType};base64,${previewAttachment.content}`}
                  alt={previewAttachment.filename}
                  className="max-w-full max-h-[400px] object-contain rounded"
                />
              </div>
            </div>
          )}

          {/* Error for failed emails */}
          {status === "failed" && email.errorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span className="font-medium">Delivery failed: </span>
                {email.errorMessage}
              </AlertDescription>
            </Alert>
          )}

          {/* Reply section */}
          {!isReplying ? (
            <div className="flex justify-end gap-2">
              {/* View Lead button - show when email has a lead and we're not already on lead details page */}
              {(email.leadId || email.lead?.id) && !leadId && (
                <Button
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                    router.push(`/leads/${email.leadId || email.lead?.id}?tab=history`);
                  }}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Lead
                </Button>
              )}
              <Button
                onClick={handleStartReply}
                disabled={checkingConnection}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {checkingConnection ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Reply className="mr-2 h-4 w-4" />
                    Reply
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Reply className="h-4 w-4" />
                <span>
                  Replying to: {email.emailType === "inbound" ? email.fromEmail : email.toEmail}
                </span>
              </div>

              <Textarea
                placeholder="Type your reply..."
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={6}
                disabled={sending}
                className="resize-none"
              />

              {/* Attachments for reply */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                  disabled={sending}
                />

                {attachmentFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {attachmentFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between p-2 bg-muted rounded-md"
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 shrink-0"
                          onClick={() => removeAttachment(index)}
                          disabled={sending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                >
                  <Paperclip className="mr-2 h-4 w-4" />
                  {attachmentFiles.length > 0 ? "Add more files" : "Attach files"}
                </Button>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsReplying(false);
                    setReplyBody("");
                    setAttachments([]);
                    setAttachmentFiles([]);
                    setError("");
                  }}
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendReply}
                  disabled={sending || !replyBody.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Reply
                      {attachmentFiles.length > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {attachmentFiles.length}
                        </Badge>
                      )}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Error when not replying */}
          {error && !isReplying && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

export default EmailDetailModal;
